import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import TopNavbar from "../components/Nav/TopNavbar";
import Footer from "../components/Sections/Footer";
import AddImage2 from "../assets/img/about.png";
import NavaService from "../services/NavaService";
import SEO from "../components/SEO";

const PAGE_SIZE = 6;

export default function PropertiesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [counties, setCounties] = useState([]);
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const countyId = searchParams.get("countyId") || "";
  const q = searchParams.get("q") || "";
  const sort = searchParams.get("sort") || "recent";
  const page = parseInt(searchParams.get("page") || "1", 10);

  const [searchInput, setSearchInput] = useState(q);
  const [countyInput, setCountyInput] = useState("");
  const [countyOptionsOpen, setCountyOptionsOpen] = useState(false);

  const matchingCounties = counties.filter((county) =>
    county.name.toLowerCase().includes(countyInput.trim().toLowerCase())
  );

  const updateParams = useCallback((next) => {
    const params = { countyId, q, sort, page: "1", ...next };
    Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
    setSearchParams(params);
  }, [countyId, q, sort, setSearchParams]);

  useEffect(() => {
    if (searchInput.trim() === q) return undefined;

    const timer = setTimeout(() => {
      updateParams({ q: searchInput.trim() });
    }, 350);

    return () => clearTimeout(timer);
  }, [searchInput, q, updateParams]);

  useEffect(() => {
    NavaService.getCounties()
      .then((res) => setCounties(res.data || []))
      .catch(() => setCounties([]));
  }, []);

  useEffect(() => {
    const selectedCounty = counties.find((county) => String(county.id) === countyId);
    setCountyInput(selectedCounty ? selectedCounty.name : "");
  }, [counties, countyId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await NavaService.getProperties({
        countyId: countyId || undefined,
        q: q || undefined,
        sort: sort === "recent" ? undefined : sort,
        page,
        limit: PAGE_SIZE,
      });
      setProperties(res.data?.data || []);
      setTotal(res.data?.total || 0);
      setTotalPages(Math.max(1, Math.ceil((res.data?.total || 0) / PAGE_SIZE)));
    } catch (err) {
      setError("We couldn't load properties right now. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  }, [countyId, q, sort, page]);

  useEffect(() => { load(); }, [load]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    updateParams({ q: searchInput.trim() });
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setSearchParams({ ...(countyId && { countyId }), ...(q && { q }), ...(sort !== "recent" && { sort }), page: String(p) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <SEO
        title="Properties for Auction"
        description="Browse verified land and property listings currently available for auction across Kenya. Search by title, location, or county."
      />
      <TopNavbar />
      <Wrapper>
        <div className="container">
          <HeaderInfo>
            <h1 className="font40 extraBold">Properties for Auction</h1>
            <p className="font13">Browse land currently listed for auction, filter by county or search by title, location, or description.</p>
          </HeaderInfo>

          <Filters className="flexSpaceCenter">
            <CountyPicker>
              <CountyInput
                type="text"
                value={countyInput}
                placeholder="Search counties..."
                aria-label="Search counties"
                onFocus={() => setCountyOptionsOpen(true)}
                onChange={(e) => {
                  setCountyInput(e.target.value);
                  setCountyOptionsOpen(true);
                }}
                onBlur={() => setTimeout(() => setCountyOptionsOpen(false), 150)}
              />
              {countyOptionsOpen && (
                <CountyOptions role="listbox">
                  <CountyOption
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setCountyInput("");
                      updateParams({ countyId: "" });
                      setCountyOptionsOpen(false);
                    }}
                  >
                    All counties
                  </CountyOption>
                  {matchingCounties.map((county) => (
                    <CountyOption
                      type="button"
                      key={county.id}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setCountyInput(county.name);
                        updateParams({ countyId: String(county.id) });
                        setCountyOptionsOpen(false);
                      }}
                    >
                      {county.name}
                    </CountyOption>
                  ))}
                  {matchingCounties.length === 0 && (
                    <NoCountyOption>No counties found</NoCountyOption>
                  )}
                </CountyOptions>
              )}
            </CountyPicker>

            <SearchForm onSubmit={handleSearchSubmit}>
              <input
                type="text"
                placeholder="Search by title, location..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button type="submit" className="pointer" aria-label="Search properties" title="Search properties">
                <span className="searchIcon" aria-hidden="true" />
              </button>
            </SearchForm>

            <SortSelect value={sort} onChange={(e) => updateParams({ sort: e.target.value })} aria-label="Order properties by price">
              <option value="recent">Newest listings</option>
            </SortSelect>
          </Filters>

          {loading ? (
            <StatusText>Loading properties...</StatusText>
          ) : error ? (
            <StatusText>{error}</StatusText>
          ) : properties.length === 0 ? (
            <StatusText>No properties match your search.</StatusText>
          ) : (
            <>
              <ResultCount className="font13">{total} propert{total === 1 ? "y" : "ies"} found</ResultCount>
              <Grid>
                {properties.map((p) => (
                  <Card key={p.id} onClick={() => navigate(`/properties/${p.id}`)} className="pointer radius8">
                    <CardImg
                      src={p.cover_image ? NavaService.getPropertyImageUrl(p.id, p.cover_image) : AddImage2}
                      alt={p.title_number}
                    />
                    <CardBody>
                      <CardTitle>{p.title_number}</CardTitle>
                      <CardLocation className="font13">{p.location} · {p.county_name}</CardLocation>
                    </CardBody>
                  </Card>
                ))}
              </Grid>

              {totalPages > 1 && (
                <Pagination className="flexCenter">
                  <PageBtn disabled={page <= 1} onClick={() => goToPage(page - 1)} className="pointer">‹ Prev</PageBtn>
                  <PageNumbers aria-label="Property pages">
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                      <PageNumber
                        key={pageNumber}
                        type="button"
                        active={pageNumber === page}
                        onClick={() => goToPage(pageNumber)}
                        className="pointer"
                      >
                        {pageNumber}
                      </PageNumber>
                    ))}
                  </PageNumbers>
                  <PageBtn disabled={page >= totalPages} onClick={() => goToPage(page + 1)} className="pointer">Next ›</PageBtn>
                </Pagination>
              )}
            </>
          )}
        </div>
      </Wrapper>
      <Footer />
    </>
  );
}

const Wrapper = styled.section`
  width: 100%;
  padding-top: 120px;
  padding-bottom: 80px;
  color: #5d191a;
`;

const HeaderInfo = styled.div`
  margin-bottom: 30px;
  color: #3b1718;
  p { color: #5d191a; }
  @media (max-width: 860px) { text-align: center; }
`;

const Filters = styled.div`
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 30px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CountyPicker = styled.div`
  position: relative;
  width: 220px;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const CountyInput = styled.input`
  width: 100%;
  height: 42px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid #d8dee8;
  padding: 0 12px;
  outline: none;
  color: #5d191a;

  &:focus {
    border-color: #5d191a;
    box-shadow: 0 0 0 3px rgba(93, 25, 26, 0.12);
  }
`;

const CountyOptions = styled.div`
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #eadfe0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 18px rgba(59, 23, 24, 0.12);
`;

const CountyOption = styled.button`
  display: block;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  background: #fff;
  color: #5d191a;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus {
    background: #f8eeee;
    outline: none;
  }
`;

const NoCountyOption = styled.div`
  padding: 10px 12px;
  color: #707070;
  font-size: 13px;
`;

const SortSelect = styled.select`
  height: 42px;
  border-radius: 8px;
  border: 1px solid #d8dee8;
  padding: 0 12px;
  outline: none;
  color: #5d191a;
  background: #fff;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 8px;
  flex: 1;
  max-width: 420px;

  input {
    flex: 1;
    height: 42px;
    border-radius: 8px;
    border: 1px solid #d8dee8;
    padding: 0 14px;
    outline: none;
  }
  button {
    height: 42px;
    width: 42px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background: #5d191a;
    color: #fff;
    font-weight: 700;
    font-size: 18px;
    line-height: 1;
    &:hover { background: #421214; }

    .searchIcon {
      position: relative;
      display: inline-block;
      width: 14px;
      height: 14px;
      border: 2px solid #fff;
      border-radius: 50%;
      box-sizing: border-box;
    }

    .searchIcon::after {
      content: "";
      position: absolute;
      width: 7px;
      height: 2px;
      right: -6px;
      bottom: -3px;
      background: #fff;
      border-radius: 2px;
      transform: rotate(45deg);
      transform-origin: left center;
    }
  }
`;

const StatusText = styled.p`
  text-align: center;
  color: #707070;
  padding: 60px 0;
`;

const ResultCount = styled.p`
  color: #707070;
  margin: 0 0 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 960px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  border: 1px solid #eef1f6;
  overflow: hidden;
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 24px rgba(19, 54, 92, 0.1);
  }
`;

const CardImg = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  display: block;
`;

const CardBody = styled.div`
  padding: 16px;
`;

const CardTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  color: #3b1718;
`;

const CardLocation = styled.p`
  margin: 0 0 8px;
  color: #6f4b4b;
`;

const Pagination = styled.div`
  gap: 20px;
  margin-top: 40px;
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
`;

const PageNumber = styled.button`
  min-width: 34px;
  height: 34px;
  border: 1px solid #5d191a;
  border-radius: 6px;
  background: ${(p) => (p.active ? "#5d191a" : "#fff")};
  color: ${(p) => (p.active ? "#fff" : "#5d191a")};
  font-weight: 700;
  cursor: pointer;
`;

const PageBtn = styled.button`
  border: 1px solid #5d191a;
  background: #fff;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 700;
  color: #5d191a;
  &:hover:not(:disabled) { background: #5d191a; color: #fff; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
